import { Tabset } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import React, {
  Children,
  FC,
  PropsWithChildren,
  ReactElement,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from 'react';

interface MdxTabProps {
  title: string;
  children?: React.ReactNode;
}

const MdxTab: FC<PropsWithChildren<MdxTabProps>> = ({ children }) => {
  // Tab contents are rendered by the parent Tabs component
  return <>{children}</>;
};

MdxTab.displayName = 'MdxTab';

const MdxTabs: FC<PropsWithChildren> = ({ children }) => {
  // Build the tab list once per `children` change; we used to recompute it on
  // every render, which fed TabAnimator a fresh `tabs` prop on every parent
  // re-render and made its `useTransition`-based content swap behave
  // unpredictably (clicks updated state but the visible content never
  // refreshed). Memoising stabilises every downstream identity.
  const tabs = useMemo(() => {
    const collected: Array<{
      id: string;
      label: string;
      contents: React.ReactNode;
    }> = [];
    Children.forEach(children, (child, index) => {
      if (!isValidElement(child)) {
        return;
      }
      const props = child.props as Record<string, unknown>;
      const title = (props.title as string) || `Tab ${index + 1}`;
      // Include index so duplicate titles (or titles that slug to the same
      // string, e.g. special-char-only) still produce unique tab ids within
      // one <Tabs> group.
      const id = `${index}-${title.toLowerCase().replace(/\s+/g, '-')}`;
      collected.push({
        id,
        label: title,
        contents: (child as ReactElement).props.children,
      });
    });
    return collected;
  }, [children]);

  const [currentTabId, setCurrentTabId] = useState<string>(tabs[0]?.id || '');

  const onTabChanged = useCallback((newTabId: string) => {
    setCurrentTabId(newTabId);
  }, []);

  const currentContents = useMemo(() => {
    return tabs.find((tab) => tab.id === currentTabId)?.contents ?? null;
  }, [tabs, currentTabId]);

  if (tabs.length === 0) {
    return null;
  }

  // Render the active tab's content directly (no TabAnimator). The animator
  // delays mounting via a useEffect-driven `isClient` flag and SSR-vs-client
  // branch swap that confuses `next-mdx-remote`'s hydration — clicks updated
  // state but the swap never landed in the DOM. A plain conditional render
  // matches what the Contentful path already does for tab content.
  // Top + bottom margin so the tab strip and its content don't sit
  // flush against the surrounding paragraphs (matches the spacing
  // markdown tables and code blocks already get from their adapters).
  return (
    <Box margin={{ vertical: '3x' }}>
      <Tabset
        smallTabsetItem={true}
        tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
        currentTabId={currentTabId}
        onTabChanged={onTabChanged}
      />
      <div role="tabpanel">{currentContents}</div>
    </Box>
  );
};

MdxTabs.displayName = 'MdxTabs';

export { MdxTabs, MdxTab };
